import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useIntl, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import qs from 'query-string';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';

import List from 'src/componentes/list';
import ListColumn from 'src/componentes/list/list-column';
import ContentManager from 'src/componentes/content-manager';
import { capitalizeFirstLetter } from 'src/utils/string';

import SearchSchema from './search-schema';
import Search from './search';
import ReuniaoEdit from './edit';
import StatusBadge from 'src/componentes/StatusBadge';
import {
  ONBOARD_ORIGEM,
  ACAO_INTERACAO_ONBOARD,
  MODULOS,
} from 'src/utils/constants';
import HintTour from 'src/components/HintTour/HintTour';
import ProactiveTourBox from 'src/components/ProactiveTour/ProactiveTour';
import reuniaoPageSteps from 'src/tours/ReuniaoPageSteps';
import Badge from 'src/componentes/Badge';

function Reuniao(props) {
  const intl = useIntl();
  const { disabled, resources, moduloAtual } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [showProactiveTour, setShowProactiveTour] = useState(false);
  const [, setOpenHelp] = useState(false);
  const [jaExisteFeedbackReuniao, setJaExisteFeedbackReuniao] = useState(null);
  const [showHintTour, setShowHintTour] = useState(false);
  const [showProactiveTourDelayed, setShowProactiveTourDelayed] =
    useState(false);
  const [isLoadingTour, setIsLoadingTour] = useState(false);

  const timeoutProactiveTourRef = useRef(null);

  const openDetail = (item) => {
    ContentManager.addContent(<ReuniaoEdit item={item} />);
  };

  const handleDetailClick = (item) => openDetail(item);

  const renderAssunto = useCallback((item) => {
    if (!item.assunto) return;
    return (
      <a onClick={() => handleDetailClick(item)} style={{ cursor: 'pointer' }}>
        {item.assunto}
      </a>
    );
  }, []);

  const renderTags = (item) =>
    item.tags?.map((x) => (
      <Badge pill className="text-white" style={{ background: x.cor }}>
        {x.descricao}
      </Badge>
    ));

  const renderStatus = (item) => {
    const status = item?.statusReuniao;
    if (!status) return null;

    return (
      <StatusBadge
        text={status.descricao}
        backgroundColor={status.corFundo}
        textColor={status.cor}
      />
    );
  };

  const getFeedbackTour = useCallback(async () => {
    try {
      const response = await http.post(
        'FeedbackTour/ObterFeedbackPorColaborador'
      );
      setJaExisteFeedbackReuniao(response.data.value.jaExisteFeedbackReuniao);
    } catch (err) {
      console.error('Erro ao obter feedback tour:', err);
    }
  }, []);

  const fetchOnboardAccess = useCallback(async () => {
    try {
      setIsLoadingTour(true);
      const response = await http.post(`/Onboard/ObterDadosAcessoOnboard`, {
        onboardOrigemId: ONBOARD_ORIGEM.CADASTRO_REUNIOES,
      });
      setShowProactiveTour(!!response?.data);
    } catch (err) {
      console.error('Erro ao obter acesso onboard:', err);
      errorHandler(err, intl);
    } finally {
      setIsLoadingTour(false);
    }
  }, [intl]);

  const saveOnboardAccess = async (acaoInteracaoOnboardId) => {
    setIsLoading(true);
    try {
      await http.post(`/Onboard/SalvarAcessoOnboardRedis`, {
        onboardOrigemId: ONBOARD_ORIGEM.CADASTRO_REUNIOES,
        acaoInteracaoOnboardId,
      });
    } catch (err) {
      errorHandler(err, intl);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeeLater = async () => {
    await saveOnboardAccess(ACAO_INTERACAO_ONBOARD.VER_DEPOIS);
    setShowProactiveTour(false);
    setShowHintTour(true);
  };

  const handleClose = async () => {
    await saveOnboardAccess(ACAO_INTERACAO_ONBOARD.FECHAR);
    setShowProactiveTour(false);
    setShowHintTour(true);
  };

  const handleCloseTour = async () => {
    await saveOnboardAccess(ACAO_INTERACAO_ONBOARD.INICIAR_TOUR);
    setShowProactiveTour(false);
    setShowHintTour(true);
  };

  useEffect(() => {
    const id = Number(qs.parse(window.location.search).id);
    if (!id || Number.isNaN(id)) return;

    ContentManager.addContent(<ReuniaoEdit item={{ id }} />);
  }, []);

  useEffect(() => {
    getFeedbackTour();
    fetchOnboardAccess();
    return () => {
      if (timeoutProactiveTourRef.current) {
        clearTimeout(timeoutProactiveTourRef.current);
      }
    };
  }, [getFeedbackTour, fetchOnboardAccess]);

  useEffect(() => {
    if (showProactiveTour) {
      timeoutProactiveTourRef.current = setTimeout(() => {
        setShowProactiveTourDelayed(true);
      }, 3000);
    } else {
      setShowProactiveTourDelayed(false);
      if (timeoutProactiveTourRef.current) {
        clearTimeout(timeoutProactiveTourRef.current);
      }
    }
  }, [showProactiveTour]);

  const isLoadingFeedback = jaExisteFeedbackReuniao === null;

  const titleTourBox = intl.formatMessage({
    id: 'facaUmTourPelaTelaCadastroReuniao',
  });
  const descriptionTourBox = capitalizeFirstLetter(
    intl.formatMessage({ id: 'registreGerenciaReunioes' })
  );

  const reuniaoSteps = reuniaoPageSteps(
    intl,
    resources,
    jaExisteFeedbackReuniao,
    isLoadingFeedback
  );

  return (
    <>
      {(moduloAtual.id === MODULOS.GESTAO_ESTRATEGICA ||
        moduloAtual.id === MODULOS.REMUNERACAO_VARIAVEL ||
        moduloAtual.id === MODULOS.MATRIZ_ORCAMENTARIA ||
        moduloAtual.id === MODULOS.GESTAO_RISCO ||
        moduloAtual.id === MODULOS.ESCRITORIO_PROJETOS) &&
        !isLoadingTour &&
        !isLoading &&
        showProactiveTourDelayed && (
          <ProactiveTourBox
            intl={intl}
            title={titleTourBox}
            description={descriptionTourBox}
            onSeeLater={handleSeeLater}
            onClose={handleClose}
            onCloseTour={handleCloseTour}
            setOpenHelp={setOpenHelp}
            steps={reuniaoSteps}
            redirectPath="/minhas-tarefas/valor-desatualizado"
            tourName={capitalizeFirstLetter(
              intl.formatMessage(
                { id: 'atualizarValores' },
                { valores: resources.valores }
              )
            )}
          />
        )}

      {showHintTour && (
        <HintTour intl={intl} setShowHintTour={setShowHintTour} />
      )}

      <List
        url="/Reuniao"
        editComponent={ReuniaoEdit}
        showSearch
        showFilter
        showNew={!disabled}
        searchSchema={SearchSchema}
        searchComponent={<Search />}
        pageSize={20}
        tag={4006}
        filtroId={54}
        saved={false}
        sort="UltimaVersao.Nome"
        refreshOnClose={true}
        ehReuniao={true}
        {...props}
        filterDrawer
      >
        <ListColumn
          headerText={intl.formatMessage({ id: 'assunto' })}
          valueField="assunto"
          valueFunction={renderAssunto}
          sortField="UltimaVersao.Nome"
          default
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'dataInicio' })}
          valueField="dataInicioItem"
          default
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'dataTermino' })}
          valueField="dataTerminoItem"
          default
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'status' })}
          valueField="statusReuniao"
          valueFunction={renderStatus}
          default
        />
        <ListColumn
          headerText={capitalizeFirstLetter(resources.responsaveis)}
          valueField="responsaveis"
          default
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'label.participantes' })}
          valueField="participantes"
          default
        />
        <ListColumn
          headerText={capitalizeFirstLetter(resources.acoes)}
          valueField="acoes"
          default
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'local' })}
          valueField="localReuniao"
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'codigo' })}
          valueField="idIntegracao"
          sortField="UltimaVersao.IdIntegracao"
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'recorrente' })}
          valueField="utilizaRecorrencia"
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'pauta' })}
          valueField="pauta"
        />
        <ListColumn
          headerText={intl.formatMessage({ id: 'label.ata' })}
          valueField="ata"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(resources.area)}
          valueField="area"
        />
        <ListColumn
          headerText={capitalizeFirstLetter(resources.tags)}
          valueField="tags"
          valueFunction={renderTags}
        />
      </List>
    </>
  );
}

function mapStateToProps(state) {
  return {
    resources: state.user.termos,
    moduloAtual: state.user.moduloAtual,
    user: state.user,
    lang: state.user?.idioma,
    viewType: state.viewType,
  };
}

export default injectIntl(connect(mapStateToProps)(Reuniao));
